import { Link } from "react-router-dom";
import { ShieldCheck, ArrowLeft } from "lucide-react";
import Footer from "../components/Footer";

const EFFECTIVE_DATE = "February 1, 2026";

const SECTIONS = [
  {
    id: "overview",
    title: "1. Overview",
    body: (
      <>
        <p>
          This Privacy Policy explains how Nexari Health ("Nexari", "we", "us") collects, uses, shares,
          and protects information when you use the Nexari Health workforce-compliance platform and
          related services (the "Service"). By using the Service, you agree to the practices described
          here.
        </p>
        <p>
          <strong>Nexari Health is not a HIPAA-covered entity and does not process PHI.</strong> The
          Service handles only employee/workforce data — names, emails, role assignments, and
          credential documents (certifications, licenses, background checks). Please do not upload
          patient records, care plans, diagnoses, or any protected health information through the
          Service.
        </p>
      </>
    ),
  },
  {
    id: "information",
    title: "2. Information We Collect",
    body: (
      <>
        <p>We collect the following categories of information:</p>
        <ul className="list-disc pl-6 space-y-1">
          <li>
            <strong>Account information:</strong> name, email, organization name, role (agency,
            family, subcontractor, admin), and password (hashed).
          </li>
          <li>
            <strong>Customer Content:</strong> credential documents you upload (e.g., HHA
            certificates, CPR cards, TB test results, driver's licenses, background-check confirmations,
            training records), employee profiles, and internal notes.
          </li>
          <li>
            <strong>Workforce data:</strong> employee names, roles, supervisor assignments, credential
            expiration dates, and compliance scores submitted by authorized users for the purpose of
            workforce compliance tracking.
          </li>
          <li>
            <strong>Billing information:</strong> we use Stripe to process payments. Stripe collects
            your card details directly; Nexari only stores Stripe customer and subscription
            identifiers, plan selection, and billing status.
          </li>
          <li>
            <strong>Usage data:</strong> log data such as IP address, browser type, pages visited,
            referrer, and timestamps, collected for security and product improvement.
          </li>
          <li>
            <strong>Cookies and similar technologies:</strong> session cookies for authentication and
            essential site functionality.
          </li>
        </ul>
      </>
    ),
  },
  {
    id: "how-we-use",
    title: "3. How We Use Information",
    body: (
      <>
        <p>We use the information we collect to:</p>
        <ul className="list-disc pl-6 space-y-1">
          <li>Provide, operate, and improve the Service;</li>
          <li>
            Process documents and generate AI-powered summaries, classifications, compliance flags,
            and suggested replies;
          </li>
          <li>Authenticate users and protect against fraud and abuse;</li>
          <li>Process payments through Stripe;</li>
          <li>Communicate with you about your account, updates, and support requests;</li>
          <li>Comply with legal obligations and enforce our Terms of Service.</li>
        </ul>
      </>
    ),
  },
  {
    id: "ai",
    title: "4. AI Processing & Subprocessors",
    body: (
      <>
        <p>
          To deliver document analysis, smart summaries, translations, and suggested replies, Nexari
          sends Customer Content to <strong>OpenAI</strong>, our AI subprocessor. OpenAI processes the
          content solely to return results to Nexari and does not, per its API data policy, use API
          submissions to train its models.
        </p>
        <p>
          Our other key subprocessors include:
        </p>
        <ul className="list-disc pl-6 space-y-1">
          <li><strong>Stripe, Inc.</strong> — payment processing</li>
          <li><strong>MongoDB Atlas</strong> (or equivalent managed database provider) — data storage</li>
          <li><strong>Cloud hosting provider</strong> — application hosting and content delivery</li>
        </ul>
        <p>
          We require subprocessors to maintain appropriate security and confidentiality safeguards.
        </p>
      </>
    ),
  },
  {
    id: "sharing",
    title: "5. How We Share Information",
    body: (
      <>
        <p>We do not sell your personal information. We share information only:</p>
        <ul className="list-disc pl-6 space-y-1">
          <li>With subprocessors, as described above, to deliver the Service;</li>
          <li>Within your organization (e.g., agency admins can see content uploaded by users in the same agency workspace);</li>
          <li>With your explicit consent;</li>
          <li>
            When required by law, subpoena, or to protect the rights, property, or safety of Nexari,
            our users, or the public;
          </li>
          <li>
            In connection with a merger, acquisition, or sale of assets, in which case we will provide
            notice before personal information is transferred.
          </li>
        </ul>
      </>
    ),
  },
  {
    id: "retention",
    title: "6. Data Retention",
    body: (
      <>
        <p>
          We retain account information and Customer Content for as long as your account is active or
          as needed to provide the Service. After account deletion, we may retain limited data (such as
          billing records and audit logs) as required by law or for legitimate business purposes
          (typically up to seven years).
        </p>
        <p>
          You can request deletion of your account and associated content at any time by contacting
          us at the email below.
        </p>
      </>
    ),
  },
  {
    id: "security",
    title: "7. Security",
    body: (
      <>
        <p>
          We use industry-standard technical and organizational measures to protect your information,
          including encryption in transit (TLS), access controls, hashed passwords, and audit logging.
          No method of transmission or storage is 100% secure; we cannot guarantee absolute security.
        </p>
      </>
    ),
  },
  {
    id: "your-rights",
    title: "8. Your Rights",
    body: (
      <>
        <p>
          Depending on where you live, you may have rights regarding your personal information,
          including the right to access, correct, delete, or port your data, and to opt out of certain
          processing. To exercise these rights, contact us at the email below. We will respond within
          the time required by applicable law.
        </p>
        <p>
          <strong>California residents (CCPA/CPRA):</strong> you have the right to know what personal
          information we collect, request deletion, and opt out of the sale or sharing of personal
          information (we do not sell personal information).
        </p>
        <p>
          <strong>EEA / UK residents (GDPR):</strong> our legal bases for processing include contract
          performance, legitimate interest, consent, and legal obligation. You have the right to lodge
          a complaint with your supervisory authority.
        </p>
      </>
    ),
  },
  {
    id: "children",
    title: "9. Children's Privacy",
    body: (
      <>
        <p>
          The Service is not directed to children under 16, and we do not knowingly collect personal
          information from children. If you believe a child has provided us information, contact us so
          we can delete it.
        </p>
      </>
    ),
  },
  {
    id: "international",
    title: "10. International Transfers",
    body: (
      <>
        <p>
          Nexari is operated from the United States. If you access the Service from outside the U.S.,
          your information may be transferred to and processed in the U.S. and other countries where
          our subprocessors operate.
        </p>
      </>
    ),
  },
  {
    id: "changes",
    title: "11. Changes to This Policy",
    body: (
      <>
        <p>
          We may update this Privacy Policy from time to time. Material changes will be communicated by
          email or through the Service. The "Effective date" above will be updated accordingly.
        </p>
      </>
    ),
  },
  {
    id: "contact",
    title: "12. Contact Us",
    body: (
      <>
        <p>
          Privacy questions or requests? Contact us at{" "}
          <a href="mailto:nexari@nexarihealth.com" className="text-[#2E5C50] underline">nexari@nexarihealth.com</a>.
        </p>
      </>
    ),
  },
];

export default function PrivacyPage() {
  return (
    <div data-testid="privacy-page" className="min-h-screen bg-[#F9F7F4]">
      <nav className="sticky top-0 z-50 bg-[#F9F7F4]/85 backdrop-blur-xl border-b border-[#E8E6E1]">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid="privacy-logo-link">
            <div className="w-8 h-8 bg-[#2E5C50] rounded-md flex items-center justify-center">
              <ShieldCheck className="w-5 h-5 text-white" strokeWidth={1.5} />
            </div>
            <span className="font-heading font-bold text-lg tracking-tight">Nexari Health</span>
          </Link>
          <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-[#2E5C50] hover:underline" data-testid="privacy-back-link">
            <ArrowLeft className="w-4 h-4" strokeWidth={1.5} /> Back to home
          </Link>
        </div>
      </nav>

      <main className="max-w-3xl mx-auto px-6 py-14 lg:py-20">
        <p className="text-xs uppercase tracking-[0.25em] font-semibold text-[#C07A60]">Legal</p>
        <h1 className="mt-3 font-heading text-4xl sm:text-5xl tracking-tight font-light text-[#1C211F]">
          Privacy <span className="font-bold text-[#2E5C50]">Policy</span>
        </h1>
        <p className="mt-3 text-sm text-[#57605A]">Effective date: {EFFECTIVE_DATE}</p>

        <div className="mt-10 space-y-10 text-[#1C211F] leading-relaxed">
          {SECTIONS.map((s) => (
            <section key={s.id} id={s.id} data-testid={`privacy-section-${s.id}`}>
              <h2 className="font-heading font-semibold text-xl text-[#1C211F]">{s.title}</h2>
              <div className="mt-3 text-sm text-[#3E4641] space-y-3">{s.body}</div>
            </section>
          ))}
        </div>
      </main>

      <Footer />
    </div>
  );
}
